import { useRequest } from 'ahooks';
import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { FaRegArrowAltCircleLeft } from 'react-icons/fa';

import LoadingImage from '../../../components/general/LoadingImage';
import StockNews from '../../../components/stock/StockNews';
import { uiHelpers } from '../../../helpers';
import { useAnalytics } from '../../../helpers/analytics.helper';
import { ccassService } from '../../../services';
import { Analytics } from '../../../shared/types';

interface Props {
  stockCode: string;
}

const HKEXNewsViewer: React.FC<Props> = props => {
  const [queue] = useAnalytics();

  const queueShowNewsAnalytics = useCallback(
    news => {
      queue(Analytics.ActionEnum.ViewNewsFile, props.stockCode, news);
    },
    [props.stockCode, queue]
  );

  const [newsId, setNewsId] = useState(null);
  const [showFile, setShowFile] = useState(false);
  const [title, setTitle] = useState<string>(null);

  const { data, loading, run, error } = useRequest(
    ccassService.viewNewsOptions(newsId),
    { manual: true }
  );

  const showNews = useCallback(
    news => {
      setShowFile(true);
      setTitle(news.title);
      setNewsId(news.id);
      queueShowNewsAnalytics(news);
    },
    [queueShowNewsAnalytics]
  );

  useEffect(() => {
    if (newsId) {
      run();
    }
  }, [run, newsId]);

  const goBack = useCallback(() => {
    setShowFile(false);
  }, []);

  const [t, i18n] = useTranslation();

  useEffect(() => {
    if (data && !data.uploaded) {
      // if the file is not uploaded, open in new tab instead.
      window.open(data.file_url);
      goBack();
    }
  }, [data, goBack]);

  return (
    <div>
      <legend>
        {props.stockCode} {t('company-news')}
      </legend>
      {showFile && (
        <div>
          <Button variant="outline-primary" onClick={goBack}>
            <FaRegArrowAltCircleLeft />
          </Button>{' '}
          {title}
        </div>
      )}
      {showFile && (
        <>
          <LoadingImage displayAsBlock={true} loading={loading} />
          {data && !loading && (
            <iframe
              key={newsId}
              style={{
                width: '100%',
                height: '600px',
                maxHeight: '100%',
              }}
              title={title}
              src={data.file_url}
            />
          )}
          {uiHelpers.showError(t, error)}
        </>
      )}
      <div style={{ display: showFile ? 'none' : 'initial' }}>
        <StockNews
          stockCode={props.stockCode}
          language={i18n.language}
          key={`news-${props.stockCode}-${i18n.language}`}
          onClickNews={showNews}
        />
      </div>
    </div>
  );
};

export default HKEXNewsViewer;
